import { call, put, select as sagaSelect } from 'redux-saga/effects';

import * as types from './session.types';
import * as sessionResourceActions from './session.actions';
import * as kpiRowActions from '../kpi-row/kpi-row.actions';
import * as kpiRowScoreboardActions from '../kpi-row-scoreboard/kpi-row-scoreboard.actions';
import * as api from '../../api/request-config/session.api';

import { apiRequest } from '../../api/api.saga-services';
import { select } from '../../../utils/selector.utils';
import { sessionSelectors } from './session.selectors';
import { ISession } from './session.types';
import { kpiRowSelectors } from '../kpi-row/kpi-row.selectors';
import { kpiRowScoreboardSelectors } from '../kpi-row-scoreboard/kpi-row-scoreboard.selectors';
import { IKpiRowScoreboard } from '../kpi-row-scoreboard/kpi-row-scoreboard.types';
import { IKpiRow } from '../kpi-row/kpi-row.types';
import { storeMeetingArchive } from '../meeting-archives/meeting-archives.saga-service';
import { IMeetingArchive } from '../meeting-archives/meeting-archives.types';
import { meetingArchiveSelectors } from '../meeting-archives/meeting-archives.selectors';

export function* storeSession(session: types.ISession | types.ISession[]) {
  yield put(sessionResourceActions.storeSession(session));
}

export function* clearSession(sessionId: types.ISession['id']) {
  yield put(sessionResourceActions.clearSession(sessionId));

  const kpiRows = (yield sagaSelect(kpiRowSelectors.selectResourceList)) as IKpiRow[];
  const kpiRowScoreboards = (yield sagaSelect(kpiRowScoreboardSelectors.selectResourceList)) as IKpiRowScoreboard[];

  // remove all scoreboards related to the deleted session from resource state
  const sessionScoreboardIdsToRemove = kpiRowScoreboards
    .filter((scoreboard) => scoreboard.sessionId === sessionId)
    .map((scoreboard) => scoreboard.id);

  for (const kpiRow of kpiRows) {
    kpiRow.kpiRowScoreboardIds = kpiRow.kpiRowScoreboardIds.filter(
      (scoreboardId) => !sessionScoreboardIdsToRemove.includes(scoreboardId),
    );
  }
  yield put(kpiRowActions.storeKpiRow(kpiRows));
  yield put(kpiRowScoreboardActions.clearKpiRowScoreboard(sessionScoreboardIdsToRemove));
}

export function* clearAllSesions() {
  const sessionIds: types.ISession['id'][] = yield select(sessionSelectors.selectResourceIds);
  yield put(sessionResourceActions.clearSession(sessionIds));
}
/* This method sends api request and handle response */
export function* getSessions(searchParams: types.ISessionSearchParams) {
  const sessions: types.ISession[] = (yield call(apiRequest, api.getNextSessionsApi(searchParams))) as types.ISession[];
  yield call(storeSession, sessions);

  return sessions.map((session) => session.id);
}

/* This method sends api request and handle response */
export function* updateSessions(sessionId: types.ISession['id'], sessionInfo: types.ISessionUpdate) {
  const session = (yield sagaSelect(sessionSelectors.selectResourceById, sessionId) as unknown) as ISession;
  const response = (yield call(apiRequest, api.updateSessionApi(sessionId, { ...sessionInfo }))) as ISession;

  const meetingArchive = (yield sagaSelect(
    meetingArchiveSelectors.selectResourceById,
    sessionId,
  ) as unknown) as IMeetingArchive;

  yield call(storeSession, { ...session, ...response });
  yield call(storeMeetingArchive, {
    ...meetingArchive,
    ...response,
  } as IMeetingArchive);
}

export function* saveEmail(sessionId: types.ISession['id'], email: string) {
  const session = (yield call(apiRequest, api.saveEmailApi({ id: sessionId, email }))) as ISession;
  yield call(storeSession, session);
}

export function* deleteSession(sessionId: types.ISession['id']) {
  yield call(clearSession, sessionId);
}

export function* storeUpdatedSessionResource(
  session: ISession,
  data?: {
    startedBy?: string;
    currentStep?: string;
    lastStepStartedAt?: string;
    meetingMembers?: string[];
    nonMeetingMembers?: string[];
  },
) {
  yield call(storeSession, { ...session, ...data });
}
